import React from 'react';
import { FiAlertTriangle, FiCheck, FiDollarSign, FiExternalLink, FiStar, FiZap } from 'react-icons/fi';
import { Badge } from '../../../design-system';
import { openExternalUrl } from '../../../services/webviewService';

interface SmartBuyAnalysisProps {
  data: any;
  onProductClick: (productId: number) => void;
}

export const SmartBuyAnalysis: React.FC<SmartBuyAnalysisProps> = ({ data, onProductClick }) => {
  const { query, productsFound, products = [], analysis = {}, statsInfo } = data;
  const findProduct = (id: string | number) => products.find((product: any) => String(product.id) === String(id));

  const openProduct = (product: any) => {
    if (product?.id) onProductClick(product.id);
    if (product?.url) openExternalUrl(product.url);
  };

  return (
    <section className="overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white">
      <header className="border-b border-ds-100 bg-ds-navy-soft px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-ds-8 bg-ds-navy text-white"><FiZap /></span>
          <div className="min-w-0">
            <h2 className="truncate text-[15px] font-bold text-ds-ink">تحلیل هوشمند خرید: {query}</h2>
            <p className="mt-1 text-[10px] text-ds-600">
              {(productsFound || products.length).toLocaleString('fa-IR')} محصول پیدا شد
              {statsInfo?.productsAnalyzed ? ` · ${statsInfo.productsAnalyzed.toLocaleString('fa-IR')} محصول رو بررسی کردم` : ''}
              {statsInfo?.reviewsAnalyzed ? ` · ${statsInfo.reviewsAnalyzed.toLocaleString('fa-IR')} تجربه رو خوندم` : ''}
            </p>
          </div>
        </div>
      </header>

      <div className="space-y-4 p-3 app-sm:p-4">
        {analysis.summary && (
          <div className="rounded-ds-8 border-r-[3px] border-ds-navy bg-ds-navy-soft px-3 py-2.5">
            <h3 className="text-xs font-bold text-ds-ink">خلاصه تحلیل</h3>
            <p className="mt-1 text-xs leading-6 text-ds-700">{analysis.summary}</p>
          </div>
        )}

        {analysis.topProducts?.length > 0 && (
          <div>
            <div className="mb-2.5 flex items-center justify-between gap-3">
              <h3 className="text-sm font-bold text-ds-ink">بهترین محصولاتی که پیشنهاد می‌کنم</h3>
              <Badge tone="navy">TOP {analysis.topProducts.length.toLocaleString('fa-IR')}</Badge>
            </div>
            <div className="grid grid-cols-1 gap-2.5 app-lg:grid-cols-2">
              {analysis.topProducts.map((recommendation: any, index: number) => {
                const product = findProduct(recommendation.id);
                return (
                  <button key={`${recommendation.id}-${index}`} type="button" onClick={() => openProduct(product)} className="group relative overflow-hidden rounded-ds-12 border border-ds-100 bg-ds-white p-3 text-right transition-[border-color,box-shadow] hover:border-ds-200 hover:shadow-ds-1">
                    <span className="absolute left-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-ds-primary text-xs font-bold text-white">{(index + 1).toLocaleString('fa-IR')}</span>
                    <div className="flex gap-3 pl-8">
                      {product?.image && <img src={product.image} alt={recommendation.name} className="h-20 w-20 shrink-0 rounded-ds-10 object-cover" />}
                      <div className="min-w-0 flex-1">
                        <h4 className="line-clamp-2-safe text-[13px] font-bold leading-6 text-ds-ink group-hover:text-ds-primary-strong">{recommendation.name}</h4>
                        <p className="mt-1 text-sm font-bold text-ds-ink">{recommendation.price?.toLocaleString('fa-IR')} <small className="text-[10px] font-normal text-ds-600">تومان</small></p>
                        {product?.rating > 0 && <p className="mt-1 flex items-center gap-1 text-[10px] text-ds-600"><FiStar className="fill-[#ffa41c] text-[#ffa41c]" />{product.rating.toLocaleString('fa-IR')} ({product.reviewCount?.toLocaleString('fa-IR')})</p>}
                      </div>
                    </div>
                    {recommendation.reason && <p className="mt-2.5 border-t border-ds-100 pt-2.5 text-[11px] leading-5 text-ds-700"><b className="text-ds-navy">چرا این محصول رو پیشنهاد می‌کنم؟ </b>{recommendation.reason}</p>}
                    {recommendation.matchedFactors?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{recommendation.matchedFactors.map((factor: string) => <Badge key={factor} tone="success"><FiCheck />{factor}</Badge>)}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {analysis.purchaseFactors?.length > 0 && (
          <div>
            <h3 className="mb-2.5 text-sm font-bold text-ds-ink">نکات مهم برای خرید</h3>
            <div className="grid gap-2 app-lg:grid-cols-2">
              {analysis.purchaseFactors.map((factor: any, index: number) => (
                <div key={`${factor.factor}-${index}`} className="rounded-ds-10 border border-ds-100 bg-ds-canvas p-3">
                  <div className="flex items-center gap-2"><h4 className="text-xs font-bold text-ds-ink">{factor.factor}</h4><Badge tone={factor.importance === 'زیاد' ? 'danger' : factor.importance === 'متوسط' ? 'warning' : 'neutral'}>{factor.importance}</Badge></div>
                  <p className="mt-1.5 text-[11px] leading-5 text-ds-700">{factor.description}</p>
                  {factor.tips?.length > 0 && <ul className="mt-2 space-y-1 text-[10px] leading-5 text-ds-600">{factor.tips.map((tip: string) => <li key={tip} className="flex gap-1.5"><FiCheck className="mt-1 shrink-0 text-ds-success" />{tip}</li>)}</ul>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-2.5 app-lg:grid-cols-2">
          {analysis.priceAnalysis && (
            <div className="rounded-ds-10 border border-ds-100 bg-ds-white p-3">
              <h3 className="flex items-center gap-2 text-xs font-bold text-ds-ink"><FiDollarSign className="text-ds-primary" />تحلیل قیمت</h3>
              <div className="mt-2 space-y-1 text-[11px] leading-5 text-ds-700">
                {analysis.priceAnalysis.averagePrice && <p><b>میانگین قیمت: </b>{analysis.priceAnalysis.averagePrice}</p>}
                {analysis.priceAnalysis.priceRange && <p><b>بازه قیمتی: </b>{analysis.priceAnalysis.priceRange}</p>}
                {analysis.priceAnalysis.recommendation && <p className="rounded-ds-6 bg-ds-warning-soft p-2"><b>توصیه: </b>{analysis.priceAnalysis.recommendation}</p>}
              </div>
            </div>
          )}

          {analysis.brandAnalysis && (
            <div className="rounded-ds-10 border border-ds-100 bg-ds-white p-3">
              <h3 className="text-xs font-bold text-ds-ink">تحلیل برند</h3>
              {analysis.brandAnalysis.topBrands?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{analysis.brandAnalysis.topBrands.map((brand: string) => <Badge key={brand} tone="navy">{brand}</Badge>)}</div>}
              {analysis.brandAnalysis.considerations && <p className="mt-2 text-[11px] leading-5 text-ds-700">{analysis.brandAnalysis.considerations}</p>}
            </div>
          )}
        </div>

        {analysis.recommendations?.length > 0 && (
          <div className="rounded-ds-10 bg-ds-success-soft p-3">
            <h3 className="text-xs font-bold text-ds-success">توصیه‌ها</h3>
            <ul className="mt-2 space-y-1 text-[11px] leading-5 text-ds-700">{analysis.recommendations.map((item: string) => <li key={item} className="flex gap-1.5"><FiCheck className="mt-1 shrink-0 text-ds-success" />{item}</li>)}</ul>
          </div>
        )}

        {analysis.warnings?.length > 0 && (
          <div className="rounded-ds-10 bg-ds-danger-soft p-3">
            <h3 className="flex items-center gap-2 text-xs font-bold text-ds-danger"><FiAlertTriangle />نکات مهم و هشدارها</h3>
            <ul className="mt-2 space-y-1 text-[11px] leading-5 text-ds-700">{analysis.warnings.map((item: string) => <li key={item}>{item}</li>)}</ul>
          </div>
        )}

        {products.length > 0 && (
          <div>
            <h3 className="mb-2.5 text-sm font-bold text-ds-ink">همه محصولات ({products.length.toLocaleString('fa-IR')})</h3>
            <div className="grid grid-cols-2 gap-2 app-lg:grid-cols-3">
              {products.map((product: any) => (
                <button key={product.id} type="button" onClick={() => openProduct(product)} className="group min-w-0 overflow-hidden rounded-ds-10 border border-ds-100 bg-ds-white text-right hover:shadow-ds-1">
                  {product.image && <img src={product.image} alt={product.name} className="aspect-square w-full object-cover" />}
                  <div className="p-2.5">
                    <h4 className="line-clamp-2-safe text-xs font-bold leading-5 text-ds-ink">{product.name}</h4>
                    <p className="mt-1 truncate text-[10px] text-ds-500">{product.vendor} · {product.vendorCity}</p>
                    <p className="mt-2 flex items-center justify-between gap-2 text-xs font-bold text-ds-ink"><span>{product.priceFormatted}</span><FiExternalLink className="text-ds-400" /></p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
